import { Routes } from '@angular/router';

import { angRoutes } from './constants/router';
import PERMISSIONS from './constants/permissions';
import { authGuard } from './guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { roleGuard } from './guards/role.guard';

export const appRoutes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./layout/auth-layout/auth-layout.component').then(m => m.AuthLayoutComponent),
    canActivate: [guestGuard],
    children: [
      { path: '', redirectTo: angRoutes.login.path, pathMatch: 'full' },
      {
        path: angRoutes.login.path,
        loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
      },
    ],
  },
  {
    path: angRoutes.dashboard.path,
    loadComponent: () =>
      import('./layout/master-layout/master-layout.component').then(m => m.MasterLayoutComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: angRoutes.home.path, pathMatch: 'full' },
      {
        path: angRoutes.home.path,
        loadComponent: () =>
          import('./pages/home-page/home-page.component').then(m => m.HomePageComponent),
      },
      {
        path: angRoutes.reports.path,
        data: { title: 'Reports' },
        loadComponent: () =>
          import('./pages/reports/reports.component').then(m => m.ReportsComponent),
      },
      {
        path: angRoutes.deviceNew.path,
        canActivate: [roleGuard],
        data: { permission: PERMISSIONS.DEVICE, action: PERMISSIONS.ACTIONS.Add },
        loadComponent: () =>
          import('./pages/device-form/device-form.component').then(m => m.DeviceFormComponent),
      },
      {
        path: angRoutes.deviceEdit.path,
        canActivate: [roleGuard],
        data: { permission: PERMISSIONS.DEVICE, action: PERMISSIONS.ACTIONS.Edit },
        loadComponent: () =>
          import('./pages/device-form/device-form.component').then(m => m.DeviceFormComponent),
      },
      {
        path: angRoutes.deviceDetail.path,
        loadComponent: () =>
          import('./pages/device-detail/device-detail.component').then(
            m => m.DeviceDetailComponent
          ),
      },
      {
        path: angRoutes.admin.path,
        children: [
          { path: '', redirectTo: angRoutes.organizations.path, pathMatch: 'full' },
          {
            path: angRoutes.organizations.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.ORGANIZATION, action: PERMISSIONS.ACTIONS.List },
            loadComponent: () =>
              import('./pages/organizations/organizations.component').then(
                m => m.OrganizationsComponent
              ),
          },
          {
            path: angRoutes.organizationNew.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.ORGANIZATION, action: PERMISSIONS.ACTIONS.Add },
            loadComponent: () =>
              import('./pages/organization-form/organization-form.component').then(
                m => m.OrganizationFormComponent
              ),
          },
          {
            path: angRoutes.organizationDetail.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.ORGANIZATION, action: PERMISSIONS.ACTIONS.View },
            loadComponent: () =>
              import('./pages/organization-detail/organization-detail.component').then(
                m => m.OrganizationDetailComponent
              ),
          },
          {
            path: angRoutes.organizationEdit.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.ORGANIZATION, action: PERMISSIONS.ACTIONS.Edit },
            loadComponent: () =>
              import('./pages/organization-form/organization-form.component').then(
                m => m.OrganizationFormComponent
              ),
          },
          {
            path: angRoutes.users.path,
            canActivate: [roleGuard],
            data: {
              permission: PERMISSIONS.USER,
              action: PERMISSIONS.ACTIONS.List,
              title: 'Users',
              subtitle: 'Everyone who can sign in to your organizations',
              breadcrumbs: [{ label: 'Admin' }, { label: 'Users' }],
            },
            loadComponent: () =>
              import('./pages/users/users.component').then(m => m.UsersComponent),
          },
          {
            path: angRoutes.userNew.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.USER, action: PERMISSIONS.ACTIONS.Add },
            loadComponent: () =>
              import('./pages/user-form/user-form.component').then(m => m.UserFormComponent),
          },
          {
            path: angRoutes.userDetail.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.USER, action: PERMISSIONS.ACTIONS.View },
            loadComponent: () =>
              import('./pages/user-detail/user-detail.component').then(m => m.UserDetailComponent),
          },
          {
            path: angRoutes.userEdit.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.USER, action: PERMISSIONS.ACTIONS.Edit },
            loadComponent: () =>
              import('./pages/user-form/user-form.component').then(m => m.UserFormComponent),
          },
          {
            path: angRoutes.facilities.path,
            canActivate: [roleGuard],
            data: {
              permission: PERMISSIONS.ORGANIZATION,
              action: PERMISSIONS.ACTIONS.List,
              title: 'Facilities',
              subtitle: 'Every facility across your organizations',
              breadcrumbs: [{ label: 'Admin' }, { label: 'Facilities' }],
            },
            loadComponent: () =>
              import('./pages/facilities/facilities.component').then(m => m.FacilitiesComponent),
          },
          {
            path: angRoutes.facilityNew.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.ORGANIZATION, action: PERMISSIONS.ACTIONS.Add },
            loadComponent: () =>
              import('./pages/facility-form/facility-form.component').then(
                m => m.FacilityFormComponent
              ),
          },
          {
            path: angRoutes.facilityDetail.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.ORGANIZATION, action: PERMISSIONS.ACTIONS.View },
            loadComponent: () =>
              import('./pages/facility-detail/facility-detail.component').then(
                m => m.FacilityDetailComponent
              ),
          },
          {
            path: angRoutes.facilityEdit.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.ORGANIZATION, action: PERMISSIONS.ACTIONS.Edit },
            loadComponent: () =>
              import('./pages/facility-form/facility-form.component').then(
                m => m.FacilityFormComponent
              ),
          },
          {
            path: angRoutes.gateways.path,
            canActivate: [roleGuard],
            data: {
              permission: PERMISSIONS.GATEWAY,
              action: PERMISSIONS.ACTIONS.List,
              title: 'Gateways',
              subtitle: 'Every gateway across your facilities',
              breadcrumbs: [{ label: 'Admin' }, { label: 'Gateways' }],
            },
            loadComponent: () =>
              import('./pages/gateways/gateways.component').then(m => m.GatewaysComponent),
          },
          {
            path: angRoutes.gatewayNew.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.GATEWAY, action: PERMISSIONS.ACTIONS.Add },
            loadComponent: () =>
              import('./pages/gateway-form/gateway-form.component').then(
                m => m.GatewayFormComponent
              ),
          },
          {
            path: angRoutes.gatewayDetail.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.GATEWAY, action: PERMISSIONS.ACTIONS.View },
            loadComponent: () =>
              import('./pages/gateway-detail/gateway-detail.component').then(
                m => m.GatewayDetailComponent
              ),
          },
          {
            path: angRoutes.gatewayEdit.path,
            canActivate: [roleGuard],
            data: { permission: PERMISSIONS.GATEWAY, action: PERMISSIONS.ACTIONS.Edit },
            loadComponent: () =>
              import('./pages/gateway-form/gateway-form.component').then(
                m => m.GatewayFormComponent
              ),
          },
        ],
      },
      {
        path: angRoutes.userProfile.path,
        canActivate: [roleGuard],
        data: {
          permission: PERMISSIONS.USER_PROFILE,
          action: PERMISSIONS.ACTIONS.View,
          title: 'My profile',
          breadcrumbs: [
            { label: 'Device Fleet', url: angRoutes.home.route },
            { label: 'My profile' },
          ],
        },
        loadComponent: () =>
          import('./pages/user-profile/user-profile.component').then(m => m.UserProfileComponent),
      },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./layout/administrative-layout/administrative-layout.component').then(
        m => m.AdministrativeLayoutComponent
      ),
    children: [
      {
        path: angRoutes.privacy.path,
        loadComponent: () =>
          import('./pages/privacy/privacy.component').then(m => m.PrivacyComponent),
      },
      {
        path: angRoutes.terms.path,
        loadComponent: () => import('./pages/terms/terms.component').then(m => m.TermsComponent),
      },
      {
        path: angRoutes.systemRequirements.path,
        loadComponent: () =>
          import('./pages/system-requirements/system-requirements.component').then(
            m => m.SystemRequirementsComponent
          ),
      },
      {
        path: angRoutes.intellectualProperty.path,
        loadComponent: () =>
          import('./pages/intellectual-property/intellectual-property.component').then(
            m => m.IntellectualPropertyComponent
          ),
      },
      {
        path: angRoutes.instructions.path,
        loadComponent: () =>
          import('./pages/instructions-for-use/instructions-for-use.component').then(
            m => m.InstructionsForUseComponent
          ),
      },
    ],
  },
  {
    path: angRoutes.unauthorized.path,
    loadComponent: () =>
      import('./pages/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent),
  },
  {
    path: angRoutes.notFound.path,
    loadComponent: () =>
      import('./pages/not-found/not-found.component').then(m => m.NotFoundComponent),
  },
  { path: '**', redirectTo: angRoutes.notFound.route },
];
